import { memo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ImageUp } from 'lucide-react'

// Headings are styled explicitly rather than with `prose`: the admin panel does
// not load @tailwindcss/typography, so those classes are inert and Preflight
// resets every heading to inherit its parent's size — making h2/h3 render as
// plain body text. This mirrors the hierarchy of the published page
// (services/web/src/blog/components/BlogMarkdown.tsx) closely enough to judge a
// draft by, without pulling in the plugin.
const COMPONENTS: Components = {
  h1: ({ children }) => (
    // Rejected by the publish rules, so make it look wrong on sight.
    <h1 className="mt-6 border-l-4 border-amber-400 bg-amber-50 py-1 pl-3 text-2xl font-bold text-amber-900">
      {children}
      <span className="ml-2 align-middle text-xs font-normal text-amber-700">
        remove this — the title comes from the field above
      </span>
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-8 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-900 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-6 text-base font-semibold text-gray-900">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-5 text-sm font-semibold text-gray-800">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="mt-3 text-sm leading-relaxed text-gray-700">{children}</p>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-blue-700 underline underline-offset-2"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="mt-3 ml-5 list-disc space-y-1.5 text-sm text-gray-700">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-3 ml-5 list-decimal space-y-1.5 text-sm text-gray-700">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed [&>p]:mt-0">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="mt-4 border-l-2 border-gray-300 pl-4 text-sm italic text-gray-600">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-t border-gray-200" />,
  img: ({ src, alt }) => (
    <img
      src={typeof src === 'string' ? src : undefined}
      alt={alt ?? ''}
      className="mt-4 block h-auto max-w-full rounded border border-gray-200"
    />
  ),
  table: ({ children }) => (
    <div className="mt-4 overflow-x-auto rounded border border-gray-200">
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-3 py-2 text-gray-700">{children}</td>
  ),
  code: ({ className, children }) => {
    if (className?.includes('language-')) {
      return (
        <pre className="mt-4 overflow-x-auto rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed text-gray-800">
          <code className={className}>{children}</code>
        </pre>
      )
    }
    return (
      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800">
        {children}
      </code>
    )
  },
}

// Mirrors the build-time strip in @libra/features/blog: whole-line HTML comments
// are removed from prose but left alone inside fenced code. Without this the
// preview shows `<!-- IMAGE: ... -->` as literal escaped text, because
// react-markdown escapes raw HTML rather than dropping it.
const HTML_COMMENT = /^[^\S\r\n]*<!--[\s\S]*?-->[^\S\r\n]*\r?\n?/gm

function outsideFences(body: string, transform: (part: string) => string): string {
  return body
    .split(/(```[\s\S]*?```)/g)
    .map((part, index) => (index % 2 === 0 ? transform(part) : part))
    .join('')
}

/** The body as it will actually be published, with authoring notes removed. */
export function stripAuthoringComments(body: string): string {
  return outsideFences(body, part => part.replace(HTML_COMMENT, ''))
}

/** How many authoring notes were dropped, so the editor can say they will not appear. */
export function countAuthoringComments(body: string): number {
  let count = 0
  outsideFences(body, part => {
    count += part.match(HTML_COMMENT)?.length ?? 0
    return part
  })
  return count
}

/**
 * Images imported from a Google Docs draft are not in the repo yet, so the body
 * references a `/blog-images/...` path that 404s until the post is published.
 * `resolveImage` maps those paths to the in-memory data URI so the preview shows
 * the real picture, exactly where it will appear on the page.
 */
export default memo(function PreviewMarkdown({
  body,
  resolveImage,
  onReplaceImage,
  describeImage,
}: {
  body: string
  resolveImage?: (src: string) => string | undefined
  /** Called with the body's image path when the author asks to swap that image. */
  onReplaceImage?: (src: string) => void
  /** Short caption under the image, e.g. dimensions and a low-resolution note. */
  describeImage?: (src: string) => { text: string; warn?: boolean } | undefined
}) {
  const interactive = Boolean(resolveImage || onReplaceImage || describeImage)

  const components: Components = interactive
    ? {
        ...COMPONENTS,
        img: ({ src, alt }) => {
          const raw = typeof src === 'string' ? src : ''
          const resolved = resolveImage?.(raw) ?? raw
          const note = describeImage?.(raw)
          return (
            // `group` so the replace control only appears on hover, keeping the
            // preview readable while still being discoverable on the image itself.
            <span className="group relative mt-4 block">
              <img
                src={resolved}
                alt={alt ?? ''}
                className="block h-auto max-w-full rounded border border-gray-200"
              />
              {onReplaceImage && (
                <button
                  type="button"
                  onClick={() => onReplaceImage(raw)}
                  className="absolute top-2 left-2 hidden items-center gap-1.5 rounded bg-gray-900/85 px-2.5 py-1.5 text-xs font-medium text-white group-hover:inline-flex hover:bg-gray-900"
                >
                  <ImageUp className="h-3.5 w-3.5" />
                  Replace image
                </button>
              )}
              {note && (
                <span
                  className={`mt-1 block text-xs ${note.warn ? 'text-amber-700' : 'text-gray-400'}`}
                >
                  {note.text}
                </span>
              )}
            </span>
          )
        },
      }
    : COMPONENTS

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {stripAuthoringComments(body)}
    </ReactMarkdown>
  )
})
